import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePublicShare, useVerifySharePassword } from '@/hooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  ExternalLink,
  Lock,
  Bookmark,
  Eye,
  Calendar,
  Loader2,
  AlertCircle,
  ArrowLeft,
} from 'lucide-react';
import type { PublicShareResponse } from '@/types/database';

export function SharedPage() {
  const { shareId } = useParams<{ shareId: string }>();
  const [password, setPassword] = useState('');
  const [verifiedData, setVerifiedData] = useState<PublicShareResponse | null>(null);

  const { data, isLoading, error } = usePublicShare(shareId || '');
  const verifyPassword = useVerifySharePassword();

  const handleVerifyPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shareId || !password) return;

    try {
      const result = await verifyPassword.mutateAsync({ shareId, password });
      if (result.verified) {
        setVerifiedData(result);
      }
    } catch {
      // Error handled by mutation
    }
  };

  // Use verified data if available, otherwise use query data
  const shareData = verifiedData || data;

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h1 className="text-xl font-semibold mb-2">페이지를 찾을 수 없습니다</h1>
          <p className="text-muted-foreground mb-6">
            링크가 만료되었거나 잘못된 주소입니다
          </p>
          <Link to="/">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              홈으로 돌아가기
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Password required state
  if (shareData?.requiresPassword && !verifiedData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Lock className="w-6 h-6 text-muted-foreground" />
            </div>
            <h1 className="text-xl font-semibold">비밀번호가 필요합니다</h1>
            <p className="text-sm text-muted-foreground mt-1">
              이 공유 링크는 비밀번호로 보호되어 있습니다
            </p>
          </div>

          <form onSubmit={handleVerifyPassword} className="space-y-4">
            <Input
              type="password"
              placeholder="비밀번호 입력"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={verifyPassword.isPending}
            />
            {verifyPassword.error && (
              <p className="text-sm text-destructive">비밀번호가 올바르지 않습니다</p>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={!password || verifyPassword.isPending}
            >
              {verifyPassword.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              확인
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // No data
  if (!shareData || !shareData.share) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-xl font-semibold">데이터를 불러올 수 없습니다</h1>
        </div>
      </div>
    );
  }

  const { share, collection, bookmarks } = shareData;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10">
        <div className="container max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="text-lg font-semibold text-emerald-600 hover:text-emerald-700 transition-colors"
            >
              Markly
            </Link>
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                <span>{share.view_count}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="container max-w-5xl mx-auto px-4 py-8">
        {/* Collection header */}
        {collection && (
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              {collection.icon && (
                <span className="text-2xl">{collection.icon}</span>
              )}
              <h1 className="text-2xl font-bold">{collection.name}</h1>
            </div>
            {collection.description && (
              <p className="text-muted-foreground">{collection.description}</p>
            )}
            <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
              {share.owner_name && (
                <span>by {share.owner_name}</span>
              )}
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(share.created_at)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Bookmark className="w-4 h-4" />
                <span>{bookmarks.length}개 북마크</span>
              </div>
            </div>
          </div>
        )}

        {/* Single bookmark header (when sharing individual bookmark) */}
        {!collection && bookmarks.length === 1 && (
          <div className="mb-8">
            <h1 className="text-2xl font-bold mb-2">공유된 북마크</h1>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              {share.owner_name && (
                <span>by {share.owner_name}</span>
              )}
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(share.created_at)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Bookmarks grid */}
        {bookmarks.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {bookmarks.map((bookmark) => (
              <a
                key={bookmark.id}
                href={bookmark.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block p-4 rounded-lg border bg-card hover:border-primary/50 hover:shadow-md transition-all"
              >
                <div className="flex items-start gap-3">
                  {bookmark.favicon_url && (
                    <img
                      src={bookmark.favicon_url}
                      alt=""
                      className="w-5 h-5 mt-0.5 rounded"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors">
                      {bookmark.title}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <span className="truncate">{bookmark.domain}</span>
                      <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </p>
                  </div>
                </div>

                {bookmark.description && (
                  <p className="text-xs text-muted-foreground mt-3 line-clamp-2">
                    {bookmark.description}
                  </p>
                )}

                {bookmark.tags && bookmark.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {bookmark.tags.slice(0, 3).map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    {bookmark.tags.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{bookmark.tags.length - 3}
                      </Badge>
                    )}
                  </div>
                )}

                {bookmark.thumbnail_url && (
                  <img
                    src={bookmark.thumbnail_url}
                    alt=""
                    className="w-full h-32 object-cover rounded mt-3"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
              </a>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Bookmark className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">북마크가 없습니다</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t mt-auto">
        <div className="container max-w-5xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <p>
              <Link to="/" className="hover:text-foreground transition-colors">
                Markly
              </Link>
              로 공유됨
            </p>
            <p>Save what matters</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
